import { useCallback, useEffect, useState } from 'react';
import { SessionFilters, SortOptions } from './useSessionFilters';

interface DashboardPreferences {
  filters: SessionFilters;
  sortOptions: SortOptions;
  activeInsightsTab: string;
}

const STORAGE_KEY = 'quentari_dashboard_preferences';

const DEFAULT_PREFERENCES: DashboardPreferences = {
  filters: {
    phase: 'all',
    decisionArea: 'all',
    dateRange: 'all',
    searchQuery: '',
  },
  sortOptions: {
    field: 'date',
    direction: 'desc',
  },
  activeInsightsTab: 'overview',
};

export function useDashboardPreferences() {
  const [preferences, setPreferences] = useState<DashboardPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          // Merge filters to ensure all fields exist
          filters: {
            ...DEFAULT_PREFERENCES.filters,
            ...parsed.filters,
          },
          sortOptions: {
            ...DEFAULT_PREFERENCES.sortOptions,
            ...parsed.sortOptions,
          },
        };
      }
    } catch (error) {
      console.error('Error loading dashboard preferences:', error);
    }
    return DEFAULT_PREFERENCES;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving dashboard preferences:', error);
    }
  }, [preferences]);

  const updateFilters = useCallback((newFilters: SessionFilters) => {
    setPreferences((prev) => {
      // Only update if filters actually changed
      if (JSON.stringify(prev.filters) === JSON.stringify(newFilters)) {
        return prev;
      }
      return { ...prev, filters: newFilters };
    });
  }, []);

  const updateSortOptions = useCallback((newSortOptions: SortOptions) => {
    setPreferences((prev) => {
      if (JSON.stringify(prev.sortOptions) === JSON.stringify(newSortOptions)) {
        return prev;
      }
      return { ...prev, sortOptions: newSortOptions };
    });
  }, []);

  const updateActiveInsightsTab = useCallback((tab: string) => {
    setPreferences((prev) => {
      if (prev.activeInsightsTab === tab) {
        return prev;
      }
      return { ...prev, activeInsightsTab: tab };
    });
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  return {
    preferences,
    updateFilters,
    updateSortOptions,
    updateActiveInsightsTab,
    resetPreferences,
  };
}
